import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { LiquidGlass } from '@liquidglass/react'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { contactPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'

export const ContactPage: React.FC = () => {
  const isMobile = useIsMobile()
  // const { t } = useTranslation()
  const [showWeChatModal, setShowWeChatModal] = useState(false)
  const [hoveredCard, setHoveredCard] = useState<string | null>(null)

  // Handle WeChat click - open app on mobile, show modal on desktop
  const handleWeChatClick = () => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

    if (isMobile) {
      // Try to open WeChat app with the ID
      window.location.href = 'weixin://dl/chat?wxid_3v5sc9omonw812'

      // Fallback: if WeChat app doesn't open after 2 seconds, show modal
      setTimeout(() => {
        setShowWeChatModal(true)
      }, 2000)
    } else {
      // Desktop: show modal
      setShowWeChatModal(true)
    }
  }

  const heroRef = React.useRef(null)
  const contactRef = React.useRef(null)
  const isHeroInView = useInView(heroRef, { once: false, amount: 0.3 })
  const isContactInView = useInView(contactRef, { once: false, amount: 0.2 })

  // Detect prefers-reduced-motion
  const prefersReducedMotion = React.useRef(
    typeof window !== 'undefined'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false
  )

  // Use optimized scroll hook to prevent flickering
  const { scrollProgress } = useOptimizedScroll()

  // Scroll to top when component mounts
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const contactMethods = [
    {
      name: 'WhatsApp',
      description: 'Instant messaging for quick responses',
      iconPath: '/whatsapp.png',
      color: '#25D366',
      gradient: 'linear-gradient(135deg, rgba(37, 211, 102, 0.25) 0%, rgba(37, 211, 102, 0.08) 100%)',
      disabled: true,
      availability: 'Unavailable',
      workingHours: 'WhatsApp support is currently unavailable.'
    },
    {
      name: 'Email',
      description: 'Professional inquiries and detailed requests',
      iconPath: '/email.png',
      color: '#4A90E2',
      gradient: 'linear-gradient(135deg, rgba(74, 144, 226, 0.25) 0%, rgba(74, 144, 226, 0.08) 100%)',
      href: 'mailto:info@mernaki.de',
      external: false,
      availability: 'Response within 24h',
      workingHours: 'Working hours\nMon, Tue, Wed, Thu, Fri, Sat\n09:00 - 17:30'
    },
    {
      name: 'LinkedIn',
      description: 'Connect with our professional network',
      iconPath: '/linkedin.png',
      color: '#0077B5',
      gradient: 'linear-gradient(135deg, rgba(0, 119, 181, 0.25) 0%, rgba(0, 119, 181, 0.08) 100%)',
      href: 'https://linkedin.com/in/dallas-n-166a9a389',
      external: true,
      availability: 'Business networking',
      workingHours: 'Working hours\nMon, Tue, Wed, Thu, Fri, Sat\n09:00 - 17:30'
    },
    {
      name: 'WeChat',
      description: 'Chinese language support available',
      iconPath: '/wechat copy.png',
      color: '#07C160',
      gradient: 'linear-gradient(135deg, rgba(7, 193, 96, 0.25) 0%, rgba(7, 193, 96, 0.08) 100%)',
      onClick: handleWeChatClick,
      availability: '中文支持',
      workingHours: 'Working hours\nMon, Tue, Wed, Thu, Fri, Sat\n09:00 - 17:30'
    },
    {
      name: 'Support',
      description: 'Technical assistance and help desk',
      iconPath: '/customer-service.png',
      color: '#9B59B6',
      gradient: 'linear-gradient(135deg, rgba(155, 89, 182, 0.25) 0%, rgba(155, 89, 182, 0.08) 100%)',
      href: 'mailto:support@mernaki.de',
      external: false,
      availability: 'Mon-Fri 9:00-18:00 CET',
      workingHours: 'Working hours\nMon, Tue, Wed, Thu, Fri, Sat\n09:00 - 17:30'
    },
    {
      name: 'Emergency',
      description: 'Urgent matters and critical support',
      iconPath: '/emergency.png',
      color: '#E74C3C',
      gradient: 'linear-gradient(135deg, rgba(231, 76, 60, 0.25) 0%, rgba(231, 76, 60, 0.08) 100%)',
      href: 'mailto:support@mernaki.com',
      external: false,
      availability: 'Priority response',
      workingHours: 'Working hours\nMon, Tue, Wed, Thu, Fri, Sat\n09:00 - 17:30'
    }
  ]

  return (
    <>
      {/* SEO Head */}
      <SEOHead
        customTitle="Contact - Mernaki | Get in Touch"
        customDescription="Contact Mernaki for inquiries, support, and partnership opportunities. Multiple channels available including WhatsApp, Email, and LinkedIn."
        customMetadata={{
          structuredData: getStructuredData('contact')
        }}
      />

      {/* Navbar is now global - no individual page navbars needed */}

      {/* Main Content Wrapper with Rounded Bottom Corners */}
      <div
        className="min-h-screen relative"
        style={{
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          borderBottomRightRadius: scrollProgress > 0 ? `${Math.min(scrollProgress * 48, 48)}px` : '48px',
          boxShadow: scrollProgress > 0
            ? `0 ${20 + scrollProgress * 20}px ${60 + scrollProgress * 40}px rgba(0, 0, 0, ${0.2 + scrollProgress * 0.15})`
            : '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transition: 'border-radius 0.1s ease-out, box-shadow 0.2s ease-out',
        }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, 30, 0],
              }
          }
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute top-1/2 -right-40 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.3, 1],
                x: [0, -50, 0],
                y: [0, -30, 0],
              }
          }
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={
            prefersReducedMotion.current
              ? {}
              : {
                scale: [1, 1.15, 1],
                x: [0, 30, 0],
                y: [0, -20, 0],
              }
          }
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Hero Section */}
        <motion.section
          ref={heroRef}
          className="relative pt-48 pb-24"
          initial={prefersReducedMotion.current ? { opacity: 1 } : { opacity: 0 }}
          animate={isHeroInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {/* Character-by-Character Headline Animation */}
            <motion.h1
              className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight tracking-tight"
              style={{
                color: '#1A1A1A',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
              initial={prefersReducedMotion.current ? "visible" : "hidden"}
              animate={isHeroInView ? "visible" : "hidden"}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.03,
                    delayChildren: 0.2
                  }
                }
              }}
            >
              {'Get in Touch'.split('').map((char, index) => (
                <motion.span
                  key={`char-${index}`}
                  variants={{
                    hidden: { opacity: 0, x: -20, y: 10 },
                    visible: {
                      opacity: 1,
                      x: 0,
                      y: 0,
                      transition: {
                        duration: 0.5,
                        ease: [0.4, 0.0, 0.2, 1]
                      }
                    }
                  }}
                  style={{ display: 'inline-block', whiteSpace: char === ' ' ? 'pre' : 'normal' }}
                >
                  {char}
                </motion.span>
              ))}
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl max-w-2xl mx-auto leading-relaxed"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 500
              }}
              initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 1.2, ease: [0.4, 0.0, 0.2, 1] }}
            >
              Choose your preferred way to connect with us
            </motion.p>
          </div>
        </motion.section>

        {/* Contact Methods Section */}
        <section ref={contactRef} className="relative pb-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Contact Methods Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {contactMethods.map((method, index) => {
                const isHovered = hoveredCard === method.name

                return (
                  <motion.div
                    key={method.name}
                    initial={prefersReducedMotion.current ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                    animate={isContactInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
                    whileHover={!prefersReducedMotion.current ? { y: -8, scale: 1.02 } : {}}
                    onMouseEnter={() => setHoveredCard(method.name)}
                    onMouseLeave={() => setHoveredCard(null)}
                  >
                    {method.onClick ? (
                      <button
                        onClick={method.onClick}
                        className="w-full text-left group block"
                        style={{
                          cursor: 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          textDecoration: 'none'
                        }}
                      >
                        <LiquidGlass borderRadius={8} blur={0.6}>
                          <div
                            className="grid grid-cols-[30%_1fr] items-stretch relative overflow-hidden"
                            style={{
                              height: '180px',
                              width: '100%',
                              display: 'grid',
                              margin: 0,
                              padding: 0,
                              backgroundColor: 'rgba(255, 255, 255, 0.15)',
                              boxShadow: '0px 4px 30px rgba(0, 0, 0, 0.5)',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              borderRadius: '8px'
                            }}
                          >
                            {/* Left Section - Icon with Animated Gradient */}
                            <div className="h-full flex items-center justify-center relative overflow-hidden">
                              {/* Animated Gradient Background */}
                              <motion.div
                                className="absolute inset-0"
                                style={{
                                  background: method.gradient,
                                  backgroundSize: '200% 200%'
                                }}
                                animate={
                                  prefersReducedMotion.current
                                    ? {}
                                    : {
                                      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                                    }
                                }
                                transition={{
                                  duration: 8,
                                  repeat: Infinity,
                                  ease: 'easeInOut'
                                }}
                              />

                              {/* Icon */}
                              <div className="relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                                <img
                                  src={method.iconPath}
                                  alt={method.name}
                                  className="w-16 h-16 object-contain drop-shadow-lg"
                                  onError={(e) => {
                                    const target = e.currentTarget
                                    target.style.display = 'none'
                                    const parent = target.parentElement
                                    if (parent) {
                                      parent.innerHTML = `<div style="font-size: 40px; color: ${method.color}; font-weight: 600">${method.name.charAt(0)}</div>`
                                    }
                                  }}
                                />
                              </div>
                            </div>

                            {/* Right Section - Details */}
                            <div className="p-5 flex flex-col justify-between h-full relative">
                              {/* Default Content */}
                              <motion.div
                                className="flex flex-col justify-between h-full"
                                animate={{
                                  opacity: isHovered ? 0 : 1,
                                  y: isHovered ? -10 : 0
                                }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                              >
                                <div>
                                  {/* Business Name */}
                                  <div
                                    className="text-xs mb-2 opacity-60"
                                    style={{
                                      color: '#666666',
                                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                                      fontWeight: 400
                                    }}
                                  >
                                    Mernaki
                                  </div>

                                  <h3
                                    className="text-2xl mb-2"
                                    style={{
                                      color: '#1A1A1A',
                                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                      fontWeight: 600
                                    }}
                                  >
                                    {method.name}
                                  </h3>

                                  <p
                                    className="text-sm mb-3"
                                    style={{
                                      color: '#666666',
                                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                      lineHeight: '1.5'
                                    }}
                                  >
                                    {method.description}
                                  </p>
                                </div>

                                <div
                                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs self-start"
                                  style={{
                                    backgroundColor: `${method.color}15`,
                                    color: method.color,
                                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                    fontWeight: 600
                                  }}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: method.color }}></span>
                                  {method.availability}
                                </div>
                              </motion.div>

                              {/* Hover Content - Working Hours */}
                              <motion.div
                                className="absolute inset-0 p-5 flex flex-col justify-center"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{
                                  opacity: isHovered ? 1 : 0,
                                  y: isHovered ? 0 : 10
                                }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
                              >
                                <div
                                  className="text-xs mb-2 opacity-60"
                                  style={{
                                    color: '#666666',
                                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                                    fontWeight: 400
                                  }}
                                >
                                  Mernaki
                                </div>

                                <h4
                                  className="text-lg mb-3"
                                  style={{
                                    color: '#1A1A1A',
                                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                    fontWeight: 600
                                  }}
                                >
                                  {method.name}
                                </h4>

                                <div
                                  className="text-sm whitespace-pre-line"
                                  style={{
                                    color: '#666666',
                                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                    lineHeight: '1.6'
                                  }}
                                >
                                  {method.workingHours}
                                </div>
                              </motion.div>
                            </div>
                          </div>
                        </LiquidGlass>
                      </button>
                    ) : (
                      <a
                        href={method.href}
                        target={method.external ? '_blank' : undefined}
                        rel={method.external ? 'noopener noreferrer' : undefined}
                        className="w-full text-left group block"
                        style={{
                          cursor: method.disabled ? 'default' : 'pointer',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          textDecoration: 'none',
                          opacity: method.disabled ? 0.5 : 1
                        }}
                        onClick={(e) => {
                          if (method.disabled) e.preventDefault();
                        }}
                      >
                        <LiquidGlass borderRadius={8} blur={0.6}>
                          <div
                            className="grid grid-cols-[30%_1fr] items-stretch relative overflow-hidden"
                            style={{
                              height: '180px',
                              width: '100%',
                              display: 'grid',
                              margin: 0,
                              padding: 0,
                              backgroundColor: 'rgba(255, 255, 255, 0.15)',
                              boxShadow: '0px 4px 30px rgba(0, 0, 0, 0.5)',
                              border: '1px solid rgba(0, 0, 0, 0.08)',
                              borderRadius: '8px'
                            }}
                          >
                            {/* Left Section - Icon with Animated Gradient */}
                            <div className="h-full flex items-center justify-center relative overflow-hidden">
                              {/* Animated Gradient Background */}
                              <motion.div
                                className="absolute inset-0"
                                style={{
                                  background: method.gradient,
                                  backgroundSize: '200% 200%'
                                }}
                                animate={
                                  prefersReducedMotion.current
                                    ? {}
                                    : {
                                      backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                                    }
                                }
                                transition={{
                                  duration: 8,
                                  repeat: Infinity,
                                  ease: 'easeInOut'
                                }}
                              />

                              {/* Icon */}
                              <div className="relative z-10 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                                <img
                                  src={method.iconPath}
                                  alt={method.name}
                                  className="w-16 h-16 object-contain drop-shadow-lg"
                                  onError={(e) => {
                                    const target = e.currentTarget
                                    target.style.display = 'none'
                                    const parent = target.parentElement
                                    if (parent) {
                                      parent.innerHTML = `<div style="font-size: 40px; color: ${method.color}; font-weight: 600">${method.name.charAt(0)}</div>`
                                    }
                                  }}
                                />
                              </div>
                            </div>

                            {/* Right Section - Details */}
                            <div className="p-5 flex flex-col justify-between h-full relative">
                              {/* Default Content */}
                              <motion.div
                                className="flex flex-col justify-between h-full"
                                animate={{
                                  opacity: isHovered ? 0 : 1,
                                  y: isHovered ? -10 : 0
                                }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                              >
                                <div>
                                  {/* Business Name */}
                                  <div
                                    className="text-xs mb-2 opacity-60"
                                    style={{
                                      color: '#666666',
                                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                                      fontWeight: 400
                                    }}
                                  >
                                    Mernaki
                                  </div>

                                  <h3
                                    className="text-2xl mb-2"
                                    style={{
                                      color: '#1A1A1A',
                                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                      fontWeight: 600
                                    }}
                                  >
                                    {method.name}
                                  </h3>

                                  <p
                                    className="text-sm mb-3"
                                    style={{
                                      color: '#666666',
                                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                      lineHeight: '1.5'
                                    }}
                                  >
                                    {method.description}
                                  </p>
                                </div>

                                <div
                                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs self-start"
                                  style={{
                                    backgroundColor: `${method.color}15`,
                                    color: method.color,
                                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                    fontWeight: 600
                                  }}
                                >
                                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: method.color }}></span>
                                  {method.availability}
                                </div>
                              </motion.div>

                              {/* Hover Content - Working Hours */}
                              <motion.div
                                className="absolute inset-0 p-5 flex flex-col justify-center"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{
                                  opacity: isHovered ? 1 : 0,
                                  y: isHovered ? 0 : 10
                                }}
                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                style={{ pointerEvents: isHovered ? 'auto' : 'none' }}
                              >
                                <div
                                  className="text-xs mb-2 opacity-60"
                                  style={{
                                    color: '#666666',
                                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                                    fontWeight: 400
                                  }}
                                >
                                  Mernaki
                                </div>

                                <h4
                                  className="text-lg mb-3"
                                  style={{
                                    color: '#1A1A1A',
                                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                    fontWeight: 600
                                  }}
                                >
                                  {method.name}
                                </h4>

                                <div
                                  className="text-sm whitespace-pre-line"
                                  style={{
                                    color: '#666666',
                                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                                    lineHeight: '1.6'
                                  }}
                                >
                                  {method.workingHours}
                                </div>
                              </motion.div>
                            </div>
                          </div>
                        </LiquidGlass>
                      </a>
                    )}
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      </div>

      {/* Footer - Conditional Mobile/Desktop */}
      {isMobile ? (
        <PageMobileFooter {...contactPageFooter} />
      ) : (
        <footer
          className="relative"
          style={{
            backgroundColor: '#0E0E0E',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            paddingTop: '140px',
            paddingBottom: '40px',
            marginTop: '-80px'
          }}
        >
          {/* Floating Gradient Divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Footer Header */}
            <div className="text-center mb-16">
              <h2
                className="text-3xl md:text-4xl"
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600,
                  letterSpacing: '-0.02em'
                }}
              >
                Let's Connect
              </h2>
            </div>

            {/* Main Footer Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">

              {/* Brand Block */}
              <div className="lg:col-span-1">
                <div className="flex items-center gap-2 mb-4">
                  <img
                    src="/Mernaki_Logo_Pack/transparent/mernaki-128.png"
                    alt="Mernaki Logo"
                    className="object-contain"
                    style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }}
                    loading="lazy"
                  />
                  <span
                    style={{
                      color: '#EAEAEA',
                      fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
                      fontSize: '20px',
                      fontWeight: 400
                    }}
                  >
                    Mernaki
                  </span>
                </div>
                <p
                  style={{
                    color: '#CFCFCF',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                  }}
                >
                  Building the future in code, design, and innovation.
                </p>
              </div>

              {/* Explore Column */}
              <div>
                <h3
                  className="mb-4"
                  style={{
                    color: '#EAEAEA',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    letterSpacing: '0.5px'
                  }}
                >
                  EXPLORE
                </h3>
                <ul className="space-y-2.5">
                  {['Home', 'About', 'Services'].map((item) => (
                    <li key={item}>
                      <Link
                        to={item === 'Home' ? '/' : `/${item.toLowerCase()}`}
                        style={{
                          color: '#CFCFCF',
                          fontSize: '14px',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          textDecoration: 'none',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        {item}
                      </Link>
                    </li>
                  ))}
                  {/* Blurred out items */}
                  {['Software', 'Hardware'].map((item) => (
                    <li key={item}>
                      <span
                        style={{
                          color: '#CFCFCF',
                          fontSize: '14px',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          filter: 'blur(4px)',
                          cursor: 'not-allowed',
                          userSelect: 'none'
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Products Column */}
              <div>
                <h3
                  className="mb-4"
                  style={{
                    color: '#EAEAEA',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    letterSpacing: '0.5px'
                  }}
                >
                  PRODUCTS
                </h3>
                <ul className="space-y-2.5">
                  {['Project Crimson', 'Project Nexus', 'Project Velocity', 'Project Quantum'].map((item) => (
                    <li key={item}>
                      <span
                        style={{
                          color: '#CFCFCF',
                          fontSize: '14px',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          filter: 'blur(4px)',
                          cursor: 'not-allowed',
                          userSelect: 'none',
                          pointerEvents: 'none'
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services Column */}
              <div>
                <h3
                  className="mb-4"
                  style={{
                    color: '#EAEAEA',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    letterSpacing: '0.5px'
                  }}
                >
                  SERVICES
                </h3>
                <ul className="space-y-2.5">
                  {/* Active service */}
                  <li>
                    <a
                      href="#"
                      style={{
                        color: '#CFCFCF',
                        fontSize: '14px',
                        fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                        textDecoration: 'none',
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      Rare Earth Permits
                    </a>
                  </li>
                  {/* Blurred out services */}
                  {['VerpackG Deletion', 'Compliance Systems', 'Recruitment Pages'].map((item) => (
                    <li key={item}>
                      <span
                        style={{
                          color: '#CFCFCF',
                          fontSize: '14px',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          filter: 'blur(4px)',
                          cursor: 'not-allowed',
                          userSelect: 'none'
                        }}
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company Column */}
              <div>
                <h3
                  className="mb-4"
                  style={{
                    color: '#EAEAEA',
                    fontSize: '14px',
                    fontWeight: 600,
                    fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                    letterSpacing: '0.5px'
                  }}
                >
                  COMPANY
                </h3>
                <ul className="space-y-2.5">
                  {[
                    { label: 'Team', href: '/team' },
                    { label: 'Press', href: '/press' },
                    { label: 'Contact', href: '/contact' },
                    { label: 'Privacy Policy', href: '/privacy' },
                    { label: 'Terms of Use', href: '/terms' }
                  ].map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        style={{
                          color: '#CFCFCF',
                          fontSize: '14px',
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                          textDecoration: 'none',
                          transition: 'opacity 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Contact Information */}
            <div
              className="mb-12 pb-12"
              style={{ borderBottom: '1px solid #222' }}
            >
              <h3
                className="mb-4"
                style={{
                  color: '#EAEAEA',
                  fontSize: '14px',
                  fontWeight: 600,
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  letterSpacing: '0.5px'
                }}
              >
                CONTACT
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div style={{ color: '#CFCFCF', fontSize: '14px', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif" }}>
                  Munich, Germany
                </div>
                <div>
                  <a
                    href="mailto:support@mernaki.de"
                    style={{
                      color: '#CFCFCF',
                      fontSize: '14px',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      textDecoration: 'none',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    support@mernaki.de
                  </a>
                </div>
                <div>
                  <a
                    href="https://www.mernaki.de"
                    style={{
                      color: '#CFCFCF',
                      fontSize: '14px',
                      fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                      textDecoration: 'none',
                      transition: 'opacity 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    www.mernaki.de
                  </a>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-center">
              <p
                style={{
                  color: '#888',
                  fontSize: '13px',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                }}
              >
                © {new Date().getFullYear()} Mernaki. All rights reserved.
              </p>
              <p
                style={{
                  color: '#666',
                  fontSize: '12px',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  marginTop: '8px'
                }}
              >
                Crafted with precision by Mernaki Studio, Germany
              </p>
            </div>
          </div>
        </footer>
      )}

      {/* WeChat Modal - Premium Glassmorphism */}
      {showWeChatModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(12px)'
          }}
          onClick={() => setShowWeChatModal(false)}
        >
          <motion.div
            className="relative max-w-md w-full backdrop-blur-xl"
            style={{
              backgroundColor: 'rgba(247, 245, 245, 0.95)',
              borderRadius: '24px',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              padding: '32px'
            }}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setShowWeChatModal(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                border: '1px solid rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)'
                e.currentTarget.style.transform = 'scale(1.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'
                e.currentTarget.style.transform = 'scale(1)'
              }}
            >
              <span style={{ fontSize: '24px', color: '#666', lineHeight: 1 }}>×</span>
            </button>

            {/* Header */}
            <div className="text-center mb-6">
              <h2
                className="text-3xl mb-2"
                style={{
                  color: '#1A1A1A',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600
                }}
              >
                WeChat Support
              </h2>
              <p
                className="text-sm"
                style={{
                  color: '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
                }}
              >
                🇨🇳 中文支持 | 🇬🇧 English Support
              </p>
            </div>

            {/* QR Code */}
            <div
              className="mb-6 p-6 rounded-2xl"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                border: '1px solid rgba(0, 0, 0, 0.05)'
              }}
            >
              <img
                src="/WeChatQR.png"
                alt="WeChat QR Code"
                className="w-full max-w-xs mx-auto object-contain rounded-xl"
                style={{ aspectRatio: '1/1' }}
              />
            </div>

            {/* WeChat ID */}
            <div
              className="mb-6 p-4 rounded-xl text-center"
              style={{
                backgroundColor: 'rgba(7, 193, 96, 0.1)',
                border: '1px solid rgba(7, 193, 96, 0.2)'
              }}
            >
              <p
                className="text-xs mb-2"
                style={{
                  color: '#666666',
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                  fontWeight: 600
                }}
              >
                WeChat ID
              </p>
              <code
                className="text-lg"
                style={{
                  color: '#07C160',
                  fontFamily: "'Monaco', 'Courier New', monospace",
                  fontWeight: 600
                }}
              >
                wxid_3v5sc9omonw812
              </code>
            </div>

            {/* Open Desktop App Button */}
            <button
              onClick={() => {
                window.location.href = 'weixin://dl/chat?wxid_3v5sc9omonw812'
              }}
              className="w-full mb-4 py-3 px-6 rounded-xl transition-all duration-200"
              style={{
                backgroundColor: 'rgba(7, 193, 96, 0.15)',
                border: '1px solid rgba(7, 193, 96, 0.3)',
                color: '#07C160',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                fontSize: '14px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(7, 193, 96, 0.25)'
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(7, 193, 96, 0.2)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(7, 193, 96, 0.15)'
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              💬 Open WeChat App
            </button>

            {/* Info Text */}
            <p
              className="text-center text-sm mb-2"
              style={{
                color: '#666666',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                lineHeight: '1.6'
              }}
            >
              Scan QR code or add WeChat ID for instant consultation
            </p>

            {/* Availability */}
            <p
              className="text-center text-xs"
              style={{
                color: '#999999',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif"
              }}
            >
              Available: Monday-Friday 9:00-18:00 CET
            </p>
          </motion.div>
        </div>
      )}
    </>
  )
}
